import { useContext, useMemo } from 'react';

import { IArticle } from '../../../boxes/models/box';
import productsPostProcessor from '../helpers/productPostProcessor';
import useProductPartnerStore from '../store/useProductPartnerStore';
import { useLifecycleStatuses } from '@/features/boxes/api/lifecyle/getLifecycle';
import { validationErrorParser } from '@/helpers/beErrorMsgParser';
import usePaginatedTableData from '@/hooks/usePaginatedTableData';
import { ArticlesContext } from '@/shared/providers/articlesProvider';

const parsingOptions = {
  errorParser: validationErrorParser,
  trackError: false,
};

const useGetProductPatner = (partnerId = '') => {
  const { data: lifecycleStatusesModel } = useLifecycleStatuses();

  const dispatch = useProductPartnerStore((state) => state.dispatch);
  const offset = useProductPartnerStore((state) => state.offset);
  const pageSize = useProductPartnerStore((state) => state.pageSize);
  const articles = useContext(ArticlesContext) as IArticle[];
  const lifecycleStatusesStore = useProductPartnerStore(
    (state) => state.productLifecycleStatuses
  ) as string[];
  const articleCodes = useProductPartnerStore(
    (state) => state.productDescription
  );

  /**
    --- Example to explain the filtering logic below --- 
    Suppose the store contains the following selected statuses:
      ['IN_BUSINESS', 'IN_BUSINESS-DAMAGED', 'IN_BUSINESS-RECALLED']
   
    And the lifecycleStatusesModel defines 'IN_BUSINESS' with sub-statuses:
      ['DAMAGED', 'RECALLED']
   
      ➤ All sub-statuses are selected, and the parent status is also selected.
      ➤ In this case, sub-statuses are redundant and will be removed.
      ➤ Final result: ['IN_BUSINESS']
   
    --- What the algorithm does ---
    1. It organizes the selected statuses by grouping sub-statuses per parent.
    2. Then, it removes sub-statuses if:
       - All sub-statuses of a parent are selected.
       - And the parent status is already selected.
    3. This prevents redundant filters being sent to the API (e.g., parent + all its sub-statuses).
    4. Then, we build a query string using the cleaned list.
    5. We also add optional article filters if any are selected.
    6. The final queryParams are passed to a paginated API hook to fetch products.
   */

  // Filter lifecycle statuses
  const productLifecycleStatuses = useMemo(() => {
    const statusObject: Record<string, string[]> = {};

    if (!lifecycleStatusesStore) return [];

    for (const item of lifecycleStatusesStore) {
      const [status, subStatus] = item.split('-');
      if (!statusObject[status]) statusObject[status] = [];
      if (subStatus && !statusObject[status].includes(subStatus)) {
        statusObject[status].push(subStatus);
      }
    }

    const lifecycleStatusesFiltered = lifecycleStatusesStore.filter((item) => {
      const [status, subStatus] = item.split('-');

      if (!subStatus) return true;

      const allSubStatusesObject = lifecycleStatusesModel?.statuses?.find(
        (s) => s.status === status
      );

      const allSubStatusesPerStatus = allSubStatusesObject?.sub_statuses || [];

      const allSubStatusesSelected =
        allSubStatusesPerStatus.length > 0 &&
        allSubStatusesPerStatus.every((ss) =>
          statusObject[status]?.includes(ss)
        );

      const hasParentStatus = lifecycleStatusesStore.includes(status);

      return !(allSubStatusesSelected && hasParentStatus);
    });

    return lifecycleStatusesFiltered;
  }, [lifecycleStatusesStore, lifecycleStatusesModel]);

  // Build the query string for lifecycle statuses
  const queryLifecycleStatuses = useMemo(() => {
    if (!productLifecycleStatuses || productLifecycleStatuses.length === 0) {
      return '';
    }

    const filters = productLifecycleStatuses.map((item) => {
      const [status, subStatus] = item.split('-');
      if (subStatus) {
        return `(product_lifecycle='${status}' AND product_lifecycle_sub_status='${subStatus}')`;
      }

      return `(product_lifecycle='${status}')`;
    });

    return filters.length === 1 ? filters[0] : filters.join(' OR ');
  }, [productLifecycleStatuses]);

  // Build the query parameters for the API request
  const queryParams = useMemo(() => {
    const hasArticleCode = articleCodes?.length > 0;

    const articleCodesFilter = hasArticleCode
      ? `(${articleCodes.map((item) => `article_code='${item}'`).join(' OR ')})`
      : '';

    let filter = '';
    if (queryLifecycleStatuses) filter += queryLifecycleStatuses;
    if (articleCodesFilter) {
      if (filter) filter += ' AND ';
      filter += articleCodesFilter;
    }

    return {
      'list_request.page_size': pageSize,
      'list_request.page_offset': offset,
      'list_request.filter': filter,
    };
  }, [articleCodes, queryLifecycleStatuses, pageSize, offset]);

  usePaginatedTableData({
    params: {
      path: `/partners/${partnerId}/products/-/state`,
      method: 'get',
      reducer: productsPostProcessor,
      reducerExtra: articles,
    },
    dispatch,
    queryParams,
    queryParamsOptions: { skipEmptyString: true },
    service: 'deviceV3',
    errorParsingOptions: parsingOptions,
  });
};

export default useGetProductPatner;
